/* ============================================================
   Hushly — theme.js
   Tiny, dependency-free progressive enhancers. Loaded deferred.
   Everything degrades gracefully without JS and respects
   prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- small helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function trapFocus(container, event) {
    var nodes = $all(FOCUSABLE, container).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- make the page behind an open modal inert ---------- */
  function setBackgroundInert(on) {
    var regions = [document.getElementById('MainContent'), document.querySelector('footer.footer-group')];
    regions.forEach(function (el) {
      if (!el) return;
      if (on) {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
      } else {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      }
    });
  }

  /* ---------- sticky header shadow ---------- */
  function initStickyHeader() {
    var header = $('[data-header]');
    if (!header || header.getAttribute('data-sticky') !== 'true') return;

    var ticking = false;
    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 4);
      ticking = false;
    }
    update();
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---------- header height -> CSS var (prevents layout shift) ---------- */
  function syncHeaderHeight() {
    var header = $('[data-header]');
    if (!header) return;
    var last = 0;
    var pending = false;
    // Guard against ResizeObserver feedback loops: only write when it really changed.
    function write(h) {
      if (Math.abs(h - last) < 1) return;
      last = h;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    // Measuring via offsetHeight forces a synchronous layout. Prefer the size the
    // ResizeObserver already computed, and batch the write into a frame.
    function fromEntry(entries) {
      var e = entries && entries[0];
      var h = 0;
      if (e && e.borderBoxSize && e.borderBoxSize.length) {
        h = e.borderBoxSize[0].blockSize;
      } else if (e && e.contentRect) {
        h = e.contentRect.height;
      }
      if (!h) return;
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () { pending = false; write(h); });
    }
    function measure() { write(header.offsetHeight); }
    measure();
    if ('ResizeObserver' in window) {
      new ResizeObserver(fromEntry).observe(header);
    } else {
      window.addEventListener('resize', measure, { passive: true });
    }
  }

  /* ---------- generic open/close panel (drawer + search) ---------- */
  function Panel(opts) {
    var panel = opts.panel;
    var openers = opts.openers || [];
    var closers = opts.closers || [];
    var overlay = opts.overlay || null;
    var onOpen = opts.onOpen;
    var lastFocused = null;
    var isOpen = false;

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      panel.hidden = false;
      // force reflow so the transition runs from the hidden state
      void panel.offsetWidth;
      panel.classList.add('is-open');
      document.body.classList.add('no-scroll');
      setBackgroundInert(true);
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
      if (typeof onOpen === 'function') onOpen(panel);
      var focusTarget = panel.querySelector('[data-autofocus]') || panel.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      panel.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
      setBackgroundInert(false);
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
      var done = function () {
        panel.hidden = true;
        panel.removeEventListener('transitionend', done);
      };
      if (reduceMotion.matches) {
        done();
      } else {
        panel.addEventListener('transitionend', done);
        // safety net if transitionend never fires
        window.setTimeout(done, 400);
      }
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    openers.forEach(function (o) {
      o.setAttribute('aria-expanded', 'false');
      o.addEventListener('click', function (e) { e.preventDefault(); open(); });
    });
    closers.forEach(function (c) {
      c.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });
    if (overlay) overlay.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') trapFocus(panel, e);
    });

    return { open: open, close: close };
  }

  function initMenuDrawer() {
    var panel = $('[data-drawer="menu"]');
    if (!panel) return;
    Panel({
      panel: panel,
      openers: $all('[data-drawer-open="menu"]'),
      closers: $all('[data-drawer-close="menu"]'),
      overlay: $('[data-drawer-overlay="menu"]')
    });
  }

  /* ---------- announcement rotator ---------- */
  var announcementRotators = [];
  var announcementVisibilityBound = false;

  function initAnnouncement(scope) {
    $all('[data-announcement]', scope || document).forEach(function (bar) {
      if (bar.dataset.announcementReady === 'true') return;
      var items = $all('[data-announcement-item]', bar);
      if (items.length < 2 || reduceMotion.matches) return;
      bar.dataset.announcementReady = 'true';

      var delay = (parseFloat(bar.getAttribute('data-rotate')) || 5) * 1000;
      var index = 0;
      var timer = null;

      function show(i) {
        items.forEach(function (item, n) {
          var active = n === i;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      }
      function next() {
        index = (index + 1) % items.length;
        show(index);
      }
      function start() { stop(); timer = window.setInterval(next, delay); }
      function stop() { window.clearInterval(timer); }

      show(0);
      start();
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);
      announcementRotators.push({ start: start, stop: stop });
    });

    /* Bind visibility handling once for all bars (avoids editor listener leak) */
    if (!announcementVisibilityBound) {
      announcementVisibilityBound = true;
      document.addEventListener('visibilitychange', function () {
        announcementRotators.forEach(function (r) {
          if (document.hidden) { r.stop(); } else { r.start(); }
        });
      });
    }
  }

  /* ---------- buy section: qty stepper + ajax add-to-cart ---------- */
  function initBuy() {
    $all('.hushly-buy__form').forEach(function (form) {
      if (form.dataset.buyReady === 'true') return;
      form.dataset.buyReady = 'true';

      function getQty() {
        var checked = form.querySelector('[name="quantity"]:checked');
        return Math.max(1, parseInt(checked && checked.value, 10) || 1);
      }

      form.addEventListener('submit', function (e) {
        var mainInput = form.querySelector('[name="id"]');
        if (!mainInput || !mainInput.value) return; // no JS-handled variant — let native form post
        e.preventDefault();
        if (form.dataset.submitting === 'true') return; // guard against double-add
        form.dataset.submitting = 'true';
        var items = [{ id: mainInput.value, quantity: getQty() }];
        var addon = form.querySelector('[data-addon-variant]');
        if (addon && addon.checked) items.push({ id: addon.getAttribute('data-addon-variant'), quantity: 1 });
        var btn = form.querySelector('[type="submit"]');
        if (btn) btn.setAttribute('aria-disabled', 'true');
        addToCart(items, function (err) {
          form.dataset.submitting = 'false';
          if (btn) btn.removeAttribute('aria-disabled');
          if (err) {
            var el = form.querySelector('[data-buy-error]');
            if (el) el.hidden = false;
          }
        });
      });
    });
  }

  /* ---------- cart: section-render refresh + drawer ---------- */
  var cartDrawerApi = null;

  function applyCartSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var doc = new DOMParser().parseFromString(sections[id], 'text/html');
      if (id === 'cart-drawer') {
        var incoming = doc.querySelector('[data-cart-contents]');
        var current = $('[data-cart-contents]');
        if (incoming && current) current.innerHTML = incoming.innerHTML;
        var newTitle = doc.querySelector('[data-cart-title-count]');
        var curTitle = $('[data-cart-title-count]');
        if (newTitle && curTitle) curTitle.textContent = newTitle.textContent;
      } else {
        // header (or any section holding the cart count badge)
        var newCount = doc.querySelector('[data-cart-count]');
        $all('[data-cart-count]').forEach(function (curCount) {
          if (!newCount) return;
          curCount.textContent = newCount.textContent;
          curCount.classList.toggle('is-empty', newCount.classList.contains('is-empty'));
        });
      }
    });
  }

  function cartSectionList() {
    return $('[data-drawer="cart"]') ? ['cart-drawer', 'header'] : ['header'];
  }

  function addToCart(items, cb) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items, sections: cartSectionList(), sections_url: window.location.pathname })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function (data) {
        applyCartSections(data.sections);
        if (cartDrawerApi) { cartDrawerApi.open(); } else { window.location.href = '/cart'; }
        if (cb) cb(null, data);
      })
      .catch(function (e) { if (cb) cb(e); });
  }

  function changeCart(line, qty) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: qty, sections: cartSectionList(), sections_url: window.location.pathname })
    })
      .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
      .then(function (data) { applyCartSections(data.sections); })
      .catch(function () {});
  }

  function initCart() {
    var panel = $('[data-drawer="cart"]');
    if (!panel) return;
    if (!cartDrawerApi) {
      cartDrawerApi = Panel({
        panel: panel,
        openers: $all('[data-drawer-open="cart"]'),
        closers: $all('[data-drawer-close="cart"]'),
        overlay: $('[data-drawer-overlay="cart"]')
      });
    }
    if (panel.dataset.cartBound === 'true') return;
    panel.dataset.cartBound = 'true';
    // Event delegation so the bound handlers survive section re-renders.
    panel.addEventListener('click', function (e) {
      var closeBtn = e.target.closest('[data-drawer-close="cart"]');
      if (closeBtn) {
        e.preventDefault();
        if (cartDrawerApi) cartDrawerApi.close();
        return;
      }
      var change = e.target.closest('[data-cart-change]');
      if (change) {
        e.preventDefault();
        changeCart(parseInt(change.getAttribute('data-line'), 10), parseInt(change.getAttribute('data-qty'), 10));
        return;
      }
      var add = e.target.closest('[data-cart-add]');
      if (add) {
        e.preventDefault();
        if (add.getAttribute('aria-disabled') === 'true') return;
        add.setAttribute('aria-disabled', 'true');
        addToCart([{ id: add.getAttribute('data-cart-add'), quantity: 1 }]);
      }
    });
  }

  /* ---------- sticky add-to-cart bar (product page) ---------- */
  function initStickyAtc() {
    $all('[data-sticky-atc]').forEach(function (bar) {
      if (bar.dataset.stickyReady === 'true') return;
      bar.dataset.stickyReady = 'true';
      var anchor = $('[data-atc-anchor]');
      var form = $('.hushly-buy__form');
      var trigger = bar.querySelector('[data-sticky-atc-btn]');
      if (trigger && form) {
        trigger.addEventListener('click', function () {
          if (form.requestSubmit) { form.requestSubmit(); }
          else { form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true })); }
        });
      }
      if (!anchor || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { bar.classList.toggle('is-visible', !e.isIntersecting); });
      }, { rootMargin: '0px 0px -20% 0px' });
      io.observe(anchor);
    });
  }

  /* ---------- Hushly PDP: bundles + gallery + add-to-cart ---------- */
  function initHushlyProduct() {
    $all('[data-hushly-product]').forEach(function (root) {
      if (root.dataset.hpReady === 'true') return;
      root.dataset.hpReady = 'true';

      var form = root.querySelector('[data-hp-form]');
      var qtyInput = root.querySelector('[data-hp-qty]');
      var addonInput = root.querySelector('[data-hp-addon]');
      var addonColors = root.querySelector('[data-hp-addon-colors]');
      var addonColorRadios = $all('[data-hp-addon-color]', root);
      var addonImg = root.querySelector('[data-hp-addon-img]');
      var addonSlides = $all('[data-hp-addon-slide]', root);
      function showAddonImage(vid) {
        if (!addonImg) return;
        addonImg.hidden = false;
        var matched = false;
        addonSlides.forEach(function (s) {
          var on = s.getAttribute('data-variant-id') === vid;
          s.classList.toggle('is-active', on);
          if (on) matched = true;
        });
        if (!matched && addonSlides.length) addonSlides[0].classList.add('is-active');
      }
      function hideAddonImage() { if (addonImg) addonImg.hidden = true; }
      function syncAddonColor(radio) {
        if (!radio) return;
        if (addonInput) addonInput.value = radio.value;
        addonColorRadios.forEach(function (r) {
          var lbl = r.closest('.addoncolor__opt');
          if (lbl) lbl.classList.toggle('sel', r === radio);
        });
        if (addonImg && !addonImg.hidden) showAddonImage(radio.value);
      }
      addonColorRadios.forEach(function (r) {
        r.addEventListener('change', function () { syncAddonColor(r); });
      });
      (function () {
        var c = null;
        addonColorRadios.forEach(function (r) { if (r.checked && !c) c = r; });
        if (!c && addonColorRadios.length) c = addonColorRadios[0];
        if (c) syncAddonColor(c);
      })();
      var nowEl = root.querySelector('[data-hp-now]');
      var wasEl = root.querySelector('[data-hp-was]');
      var saveEl = root.querySelector('[data-hp-save]');
      var stickyNowEl = root.querySelector('[data-hp-sticky-now]');
      var bundles = $all('[data-hp-bundle]', root);
      var selected = bundles[0] || null;

      function selectBundle(b) {
        selected = b;
        bundles.forEach(function (x) {
          x.classList.remove('sel');
          var r = x.querySelector('input[type="radio"]');
          if (r) r.checked = false;
        });
        b.classList.add('sel');
        var radio = b.querySelector('input[type="radio"]');
        if (radio) radio.checked = true;
        if (qtyInput) qtyInput.value = b.getAttribute('data-qty') || '1';
        if (nowEl && b.getAttribute('data-now')) nowEl.textContent = b.getAttribute('data-now');
        if (stickyNowEl && b.getAttribute('data-now')) stickyNowEl.textContent = b.getAttribute('data-now');
        if (wasEl) { var w = b.getAttribute('data-was'); if (w) { wasEl.textContent = w; wasEl.hidden = false; } else { wasEl.hidden = true; } }
        if (saveEl) { var s = b.getAttribute('data-save'); if (s) { saveEl.textContent = s; saveEl.hidden = false; } else { saveEl.hidden = true; } }
        var bAddon = b.getAttribute('data-addon') === 'true';
        if (addonColors) addonColors.hidden = !bAddon;
        if (bAddon) { showAddonImage(addonInput ? addonInput.value : null); } else { hideAddonImage(); }
      }
      bundles.forEach(function (b) {
        b.addEventListener('click', function () { selectBundle(b); });
      });
      if (selected) {
        var selAddon = selected.getAttribute('data-addon') === 'true';
        if (addonColors) addonColors.hidden = !selAddon;
        if (selAddon) { showAddonImage(addonInput ? addonInput.value : null); } else { hideAddonImage(); }
      }

      var scroller = root.querySelector('[data-hp-mainscroll]');
      var slides = $all('[data-hp-slide]', root);
      var thumbs = $all('[data-hp-thumb]', root);
      function setActiveThumb(i) { thumbs.forEach(function (t, n) { t.classList.toggle('active', n === i); }); }
      // Desktop shows one slide via opacity (is-active); mobile keeps the scroll slider.
      function setActiveSlide(idx) {
        if (idx < 0 || idx >= slides.length) return;
        slides.forEach(function (s, n) { s.classList.toggle('is-active', n === idx); });
      }
      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () {
          var idx = parseInt(thumb.getAttribute('data-index'), 10);
          if (isNaN(idx)) idx = i;
          setActiveSlide(idx);
          if (scroller && slides[idx]) scroller.scrollTo({ left: slides[idx].offsetLeft, behavior: 'smooth' });
          setActiveThumb(i);
        });
      });
      if (scroller && slides.length > 1) {
        var galRaf;
        // Cache slide offsets: reading offsetLeft for every slide on every scroll
        // frame forces a layout each time. Positions only change on resize.
        var offsets = null;
        function slideOffsets() {
          if (!offsets) offsets = slides.map(function (s) { return s.offsetLeft; });
          return offsets;
        }
        window.addEventListener('resize', function () { offsets = null; }, { passive: true });
        scroller.addEventListener('scroll', function () {
          if (galRaf) window.cancelAnimationFrame(galRaf);
          galRaf = window.requestAnimationFrame(function () {
            var pos = scroller.scrollLeft, closest = 0, min = Infinity;
            slideOffsets().forEach(function (left, n) { var d = Math.abs(left - pos); if (d < min) { min = d; closest = n; } });
            setActiveThumb(closest);
          });
        }, { passive: true });
      }

      $all('[data-hp-video]', root).forEach(function (v) {
        var pbtn = v.querySelector('[data-hp-play]');
        var video = v.querySelector('video');
        if (!pbtn || !video) return;
        pbtn.addEventListener('click', function () {
          video.setAttribute('controls', '');
          v.classList.add('is-playing');
          var pr = video.play();
          if (pr && pr.catch) pr.catch(function () {});
        });
      });

      if (form) {
        form.addEventListener('submit', function (e) {
          var variantInput = root.querySelector('[data-hp-variant]');
          if (!variantInput || !variantInput.value) return; // let native post happen
          e.preventDefault();
          if (form.dataset.submitting === 'true') return;
          form.dataset.submitting = 'true';
          var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
          var items = [{ id: variantInput.value, quantity: qty }];
          if (selected && selected.getAttribute('data-addon') === 'true' && addonInput && addonInput.value) {
            var addonQty = Math.max(1, parseInt(selected.getAttribute('data-addon-qty'), 10) || 1);
            items.push({ id: addonInput.value, quantity: addonQty });
          }
          var btn = root.querySelector('[data-hp-atc]');
          if (btn) btn.setAttribute('aria-disabled', 'true');
          addToCart(items, function (err) {
            form.dataset.submitting = 'false';
            if (btn) btn.removeAttribute('aria-disabled');
            if (err) { var el = root.querySelector('[data-hp-err]'); if (el) el.hidden = false; }
          });
        });
      }

      var shipEl = root.querySelector('[data-hp-ship]');
      if (shipEl) {
        var shipText = shipEl.querySelector('[data-hp-ship-text]');
        var cutoff = parseInt(shipEl.getAttribute('data-cutoff'), 10);
        if (isNaN(cutoff)) cutoff = 14;
        var tToday = shipEl.getAttribute('data-today') || 'bestil inden for %t, så sender vi i dag';
        var tDefault = shipEl.getAttribute('data-default') || '1-3 hverdages levering';
        var shipFmt = null;
        try { shipFmt = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Copenhagen', weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }); } catch (e) {}
        var weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        var forceShip = /[?&]shippreview=1/i.test(window.location.search || '');
        function pad2(n) { return n < 10 ? '0' + n : '' + n; }
        function updateShip() {
          var wd, h, m, s;
          if (shipFmt) {
            var parts = {};
            shipFmt.formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
            wd = parts.weekday; h = parseInt(parts.hour, 10) % 24; m = parseInt(parts.minute, 10); s = parseInt(parts.second, 10);
          } else {
            var d = new Date(); wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]; h = d.getHours(); m = d.getMinutes(); s = d.getSeconds();
          }
          if (forceShip || (weekdays.indexOf(wd) !== -1 && h < cutoff)) {
            var remain = cutoff * 3600 - (h * 3600 + m * 60 + s);
            if (remain <= 0) remain += 86400;
            var cd = pad2(Math.floor(remain / 3600)) + ':' + pad2(Math.floor((remain % 3600) / 60)) + ':' + pad2(remain % 60);
            shipText.innerHTML = tDefault + ' <span class="ship-sep">·</span> <span class="ship-today">' + tToday.replace('%t', '<strong>' + cd + '</strong>') + '</span>';
          } else {
            shipText.textContent = tDefault;
          }
        }
        updateShip();
        setInterval(updateShip, 1000);
      }

      var stickyBar = root.querySelector('[data-hp-sticky]');
      var mainCta = root.querySelector('[data-hp-atc]');
      if (stickyBar && mainCta && 'IntersectionObserver' in window) {
        var sio = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            stickyBar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0);
          });
        }, { threshold: 0 });
        sio.observe(mainCta);
      }
    });
  }

  /* ---------- testimonial marquee pause/play (WCAG 2.2.2) ---------- */
  function initMarquee() {
    $all('[data-marquee]').forEach(function (marquee) {
      if (marquee.dataset.marqueeReady === 'true') return;
      marquee.dataset.marqueeReady = 'true';
      var toggle = marquee.querySelector('[data-marquee-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', function () {
        var paused = marquee.classList.toggle('is-paused');
        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        var lbl = paused ? toggle.getAttribute('data-label-play') : toggle.getAttribute('data-label-pause');
        if (lbl) toggle.setAttribute('aria-label', lbl);
      });
    });
  }

  /* ---------- scroll reveal ---------- */
  function initReveal() {
    var nodes = $all('[data-reveal]');
    if (!nodes.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: [0, 0.1] });
    nodes.forEach(function (n) { if (!n.classList.contains('is-revealed')) io.observe(n); });
  }

  /* ---------- product gallery (buy section thumbnails) ---------- */
  function initGallery() {
    $all('[data-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';
      var main = gallery.querySelector('.hushly-buy__media img');
      var thumbs = $all('[data-gallery-thumb]', gallery);
      if (!main || thumbs.length < 2) return;
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.getAttribute('data-full');
          if (full) { main.removeAttribute('srcset'); main.src = full; }
          thumbs.forEach(function (t) { t.removeAttribute('aria-current'); });
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  /* ---------- boot ---------- */
  function init() {
    initStickyHeader();
    syncHeaderHeight();
    initMenuDrawer();
    initCart();
    initAnnouncement();
    initBuy();
    initGallery();
    initStickyAtc();
    initHushlyProduct();
    initMarquee();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init when sections are edited in the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    initStickyHeader();
    syncHeaderHeight();
    initMenuDrawer();
    initCart();
    initAnnouncement(e.target);
    initBuy();
    initGallery();
    initStickyAtc();
    initHushlyProduct();
    initMarquee();
    initReveal();
  });
})();
